import { useMemo } from "react"
import { ArrowLeft, ArrowRight } from "@untitledui/icons"
import { useEstimate as useEstimateQuery } from "@/lib/api/use-estimates"
import type {
  ApiEstimateLineItem,
  ApiOperation,
  SectionWorkType,
  VehicleSection,
} from "@/lib/api/types"
import { cx } from "@/lib/utils/cx"
import { FE_OPERATIONS_COPY } from "./copy"

// Full Estimate — Step 6 (Operations, read-only).
//
// Operations are children of line items, populated server-side by
// `runSectionsOrchestrator` after the user PUTs line-item edits on
// the Damage step. This step renders them grouped by section so the
// estimator can review what the orchestrator decided. Editing happens
// upstream (Damage) — operations re-derive automatically.

interface StepOperationsProps {
  estimateId: number
  onNext: () => void
  onBack: () => void
}

interface SectionGroup {
  section: VehicleSection
  rows: Array<{
    component: string
    side: string | null
    operations: ApiOperation[]
  }>
  totalHours: number
  totalParts: number
  totalSublet: number
}

const ALL_SECTIONS: VehicleSection[] = [
  'FRONT_BUMPER_GRILLE', 'FRONT_LAMPS', 'HOOD', 'FENDER',
  'FRONT_DOOR', 'REAR_DOOR', 'QUARTER_PANEL', 'ROCKER_PANEL',
  'ROOF', 'TRUNK_LID', 'TRUNK', 'LIFT_GATE', 'TAIL_GATE',
  'REAR_LAMPS', 'REAR_BUMPER', 'WHEELS', 'FRAME', 'PICKUP_BOX',
  'WINDSHIELD',
]

const WORK_TYPE_LABEL: Record<SectionWorkType, string> = {
  body_repair: "Body Repair",
  refinish: "Refinish",
  refinish_prep: "Refinish Prep",
  refinish_adder: "Refinish Adder",
  adas_diagnostic: "ADAS Diagnostic",
  mechanical: "Mechanical",
  shop_admin: "Shop Admin",
  sublet_other: "Sublet · Other",
}

function sectionLabel(s: string): string {
  return s.replace(/_/g, " ").toLowerCase().replace(/\b\w/g, (c) => c.toUpperCase())
}

function componentLabel(c: string): string {
  return c.replace(/_/g, " ").toLowerCase().replace(/\b\w/g, (c) => c.toUpperCase())
}

function fmtCurrency(n: number): string {
  return new Intl.NumberFormat("en-US", {
    style: "currency",
    currency: "USD",
    maximumFractionDigits: 0,
  }).format(n)
}

export function StepOperations({ estimateId, onNext, onBack }: StepOperationsProps) {
  const { data: estimate } = useEstimateQuery(estimateId, {
    refetchInterval: false,
  })

  const groups: SectionGroup[] = useMemo(() => {
    if (!estimate) return []
    const map = new Map<string, SectionGroup>()
    for (const li of estimate.lineItems ?? []) {
      const sectionKey = String(li.section ?? "MISCELLANEOUS")
      const ops = li.operations ?? []
      if (!map.has(sectionKey)) {
        map.set(sectionKey, {
          section: li.section,
          rows: [],
          totalHours: 0,
          totalParts: 0,
          totalSublet: 0,
        })
      }
      const group = map.get(sectionKey)!
      group.rows.push({
        component: li.component,
        side: li.side,
        operations: ops,
      })
      for (const op of ops) {
        group.totalHours += op.laborHours || 0
        group.totalParts += op.partPrice || 0
        group.totalSublet += op.subletAmount || 0
      }
    }
    const ordered: SectionGroup[] = []
    for (const s of ALL_SECTIONS) {
      if (map.has(s)) {
        ordered.push(map.get(s)!)
        map.delete(s)
      }
    }
    for (const v of map.values()) ordered.push(v)
    return ordered
  }, [estimate])

  const totalHours = groups.reduce((sum, g) => sum + g.totalHours, 0)
  const totalParts = groups.reduce((sum, g) => sum + g.totalParts, 0)

  if (!estimate) {
    return (
      <div className="rounded-2xl border border-zinc-200 bg-white p-10">
        <div className="flex flex-col items-center text-center gap-3">
          <div className="size-10 border-[3px] border-zinc-200 border-t-zinc-900 rounded-full animate-spin" />
          <p className="text-[13.5px] text-zinc-500">{FE_OPERATIONS_COPY.loading}</p>
        </div>
      </div>
    )
  }

  const hasAnyOps = groups.some((g) => g.rows.some((r) => r.operations.length > 0))

  return (
    <div className="space-y-5">
      <header>
        <p className="text-[11px] font-bold uppercase tracking-[0.16em] text-emerald-600 mb-3">
          {FE_OPERATIONS_COPY.eyebrow}
        </p>
        <h2 className="text-2xl md:text-[28px] font-bold text-zinc-900 tracking-tight leading-tight">
          {FE_OPERATIONS_COPY.title}
        </h2>
        <p className="mt-1.5 text-[14px] text-zinc-500 leading-relaxed">
          {FE_OPERATIONS_COPY.subtitle}
        </p>
      </header>

      {/* Read-only banner so the user knows this isn't editable. */}
      <div className="rounded-xl border border-amber-200 bg-amber-50/70 px-4 py-2.5 text-[12.5px] text-amber-900 leading-relaxed">
        {FE_OPERATIONS_COPY.readOnlyHint}
      </div>

      {!hasAnyOps && (
        <div className="rounded-2xl border border-zinc-200 bg-white p-8 text-center">
          <p className="text-[13.5px] text-zinc-500">
            {FE_OPERATIONS_COPY.empty}
          </p>
        </div>
      )}

      {groups.map((group) => (
        <SectionCard key={group.section} group={group} />
      ))}

      {/* Footer summary */}
      {hasAnyOps && (
        <div className="rounded-2xl border border-zinc-200 bg-zinc-50 px-5 py-4 flex items-center justify-between gap-4">
          <div className="text-[12.5px] text-zinc-600">
            <span className="font-semibold text-zinc-900 tabular-nums">
              {totalHours.toFixed(1)}
            </span>
            {" "}labor hrs
            <span className="text-zinc-300 mx-2">·</span>
            <span className="font-semibold text-zinc-900 tabular-nums">
              {fmtCurrency(totalParts)}
            </span>
            {" "}parts
          </div>
        </div>
      )}

      {/* CTA bar */}
      <div className="flex flex-col-reverse sm:flex-row sm:justify-between gap-2 pt-2">
        <button
          type="button"
          onClick={onBack}
          className="h-12 px-5 rounded-2xl border border-zinc-200 bg-white hover:bg-zinc-50 text-[13.5px] font-semibold text-zinc-800 transition-colors duration-150 flex items-center justify-center gap-1.5"
        >
          <ArrowLeft className="size-4" aria-hidden />
          {FE_OPERATIONS_COPY.back}
        </button>
        <button
          type="button"
          onClick={onNext}
          className="h-12 px-5 rounded-2xl bg-zinc-900 hover:bg-black text-white text-[13.5px] font-semibold transition-colors duration-150 flex items-center justify-center gap-1.5 shadow-lg shadow-zinc-900/15"
        >
          {FE_OPERATIONS_COPY.continue}
          <ArrowRight className="size-4" aria-hidden />
        </button>
      </div>
    </div>
  )
}

function SectionCard({ group }: { group: SectionGroup }) {
  const opCount = group.rows.reduce((n, r) => n + r.operations.length, 0)
  return (
    <div className="rounded-2xl border border-zinc-200 bg-white overflow-hidden">
      <div className="flex items-center justify-between gap-3 px-5 py-3.5 bg-zinc-50/60 border-b border-zinc-100">
        <div className="flex items-center gap-2.5">
          <h3 className="text-[14px] font-bold text-zinc-900 tracking-tight">
            {sectionLabel(group.section)}
          </h3>
          <span className="text-[11.5px] font-semibold text-zinc-500 px-2 h-5 rounded-full bg-zinc-100 inline-flex items-center">
            {opCount} {opCount === 1 ? "op" : "ops"}
          </span>
        </div>
        {group.totalHours > 0 && (
          <span className="text-[11.5px] text-zinc-500 tabular-nums">
            {group.totalHours.toFixed(1)} hrs
          </span>
        )}
      </div>
      <div className="divide-y divide-zinc-100">
        {group.rows.map((row, idx) => (
          <ComponentBlock key={idx} row={row} />
        ))}
      </div>
    </div>
  )
}

function ComponentBlock({
  row,
}: {
  row: { component: string; side: string | null; operations: ApiOperation[] }
}) {
  return (
    <div className="px-5 py-3.5">
      <div className="flex items-center justify-between gap-3 mb-2">
        <div className="text-[13.5px] font-semibold text-zinc-900">
          {componentLabel(row.component)}
          {row.side && (
            <span className="ml-2 text-[11.5px] font-medium text-zinc-500 uppercase tracking-wider">
              {row.side.replace(/_/g, " ")}
            </span>
          )}
        </div>
      </div>
      {row.operations.length === 0 ? (
        <p className="text-[12px] text-zinc-400 italic">
          No operations yet
        </p>
      ) : (
        <ul className="space-y-1">
          {row.operations.map((op, idx) => (
            <li key={idx} className="flex items-start justify-between gap-3">
              <div className="flex-1 min-w-0">
                <div className={cx(
                  "text-[11.5px] font-bold uppercase tracking-wider",
                  workTypeColor(op.workType),
                )}>
                  {WORK_TYPE_LABEL[op.workType] ?? op.workType}
                </div>
                <p className="text-[12.5px] text-zinc-700 leading-relaxed mt-0.5">
                  {op.description}
                </p>
              </div>
              <div className="text-right text-[11.5px] text-zinc-500 shrink-0 tabular-nums">
                {op.laborHours > 0 && <div>{op.laborHours.toFixed(1)} hr</div>}
                {op.partPrice > 0 && <div>{fmtCurrency(op.partPrice)}</div>}
                {(op.subletAmount ?? 0) > 0 && (
                  <div>{fmtCurrency(op.subletAmount ?? 0)} sublet</div>
                )}
              </div>
            </li>
          ))}
        </ul>
      )}
    </div>
  )
}

function workTypeColor(t: SectionWorkType): string {
  switch (t) {
    case "body_repair":
      return "text-orange-700"
    case "refinish":
    case "refinish_prep":
    case "refinish_adder":
      return "text-indigo-700"
    case "adas_diagnostic":
    case "mechanical":
      return "text-cyan-700"
    case "shop_admin":
      return "text-zinc-500"
    case "sublet_other":
      return "text-rose-700"
  }
}
